import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, EmptyError, finalize, lastValueFrom, Observable, of, Subject, takeUntil, throwError } from 'rxjs';
import { ToastService } from '../../components/toast/service/toast.service';

type TypeT = {
  headers?: HttpHeaders | {
    [header: string]: string | string[];
  };
  cancelInterceptors?: boolean;
  abortKey?: string;
  context?: HttpContext;
  params?: HttpParams | {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
  transferCache?: {
    includeHeaders?: string[];
  } | boolean;
  signal?: AbortSignal;
}

export type ApiResult<T> = {
  error?: HttpErrorResponse;
  success?: T;
  canceled?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class Api {

  private readonly httpClient: HttpClient = inject(HttpClient);

  private readonly httpBackend: HttpBackend = inject(HttpBackend);

  private readonly toastService: ToastService = inject(ToastService);

  private aborts: {
    key: string;
    subject$: Subject<void>;
  }[] = [];


  public getAsync<T>(url: string, config?: TypeT): Promise<ApiResult<T>> {
    const client = this.getClient(config?.cancelInterceptors);
    const key = config?.abortKey ?? url;

    return this.toAsyncResult(this.executePromise<T>(() => client.get<T>(url, config), key, config?.signal));
  }

  public getBlobAsync(url: string, config?: Omit<TypeT, 'responseType'>): Promise<ApiResult<Blob>> {
    const client = this.getClient(config?.cancelInterceptors);
    const key = config?.abortKey ?? url;

    return this.toAsyncResult(this.executePromise<Blob>(() => client.get(url, { ...config, responseType: 'blob' }), key, config?.signal));
  }

  public get<T>(url: string, config?: TypeT): Observable<T | null> {
    const client = this.getClient(config?.cancelInterceptors);
    const key = config?.abortKey ?? url;

    return this.execute<T>(() => client.get<T>(url, config), key, config?.signal);
  }

  public async putAsync<T>(url: string, body?: any, config?: TypeT): Promise<ApiResult<T>> {
    const client = this.getClient(config?.cancelInterceptors);
    const key = config?.abortKey ?? url;

    return this.toAsyncResult(this.executePromise<T>(() => client.put<T>(url, (body ?? {}), config), key, config?.signal));
  }

  public put<T>(url: string, body?: any, config?: TypeT): Observable<T | null> {
    const client = this.getClient(config?.cancelInterceptors);
    const key = config?.abortKey ?? url;

    return this.execute<T>(() => client.put<T>(url, (body ?? {}), config), key, config?.signal);
  }

  public async postAsync<T>(url: string, body?: any, config?: TypeT): Promise<ApiResult<T>> {
    const client = this.getClient(config?.cancelInterceptors);
    const key = config?.abortKey ?? url;

    return this.toAsyncResult(this.executePromise<T>(() => client.post<T>(url, (body ?? {}), config), key, config?.signal));
  }

  public post<T>(url: string, body?: any, config?: TypeT): Observable<T | null> {
    const client = this.getClient(config?.cancelInterceptors);
    const key = config?.abortKey ?? url;

    return this.execute<T>(() => client.post<T>(url, (body ?? {}), config), key, config?.signal);
  }

  public async patchAsync<T>(url: string, body?: any, config?: TypeT): Promise<ApiResult<T>> {
    const client = this.getClient(config?.cancelInterceptors);
    const key = config?.abortKey ?? url;

    return this.toAsyncResult(this.executePromise<T>(() => client.patch<T>(url, (body ?? {}), config), key, config?.signal));
  }

  public patch<T>(url: string, body?: any, config?: TypeT): Observable<T | null> {
    const client = this.getClient(config?.cancelInterceptors);
    const key = config?.abortKey ?? url;

    return this.execute<T>(() => client.patch<T>(url, (body ?? {}), config), key, config?.signal);
  }

  public async deleteAsync<T>(url: string, config?: TypeT): Promise<ApiResult<T>> {
    const client = this.getClient(config?.cancelInterceptors);
    const key = config?.abortKey ?? url;

    return this.toAsyncResult(this.executePromise<T>(() => client.delete<T>(url, config), key, config?.signal));
  }

  public delete<T>(url: string, config?: TypeT): Observable<T | null> {
    const client = this.getClient(config?.cancelInterceptors);
    const key = config?.abortKey ?? url;

    return this.execute<T>(() => client.delete<T>(url, config), key, config?.signal);
  }

  public abort(key: string): void {
    const abortIndex = this.aborts.findIndex(x => x.key === key);

    if (abortIndex === -1) return;

    this.aborts[abortIndex]?.subject$.next();
    this.aborts[abortIndex]?.subject$.complete();

    this.aborts.splice(abortIndex, 1);
  }

  public removeAbort(key: string): void {
    const abortIndex = this.aborts.findIndex(x => x.key === key);

    if (abortIndex === -1) return;

    this.aborts.splice(abortIndex, 1);
  }

  public abortAll(): void {
    this.aborts.forEach(x => x.subject$.next());
    this.aborts.forEach(x => x.subject$.complete());

    this.aborts = [];
  }

  private async executePromise<T>(method: () => Observable<T>, abortKey: string, signal?: AbortSignal): Promise<T | null> {
    const abort$ = this.newAbort(abortKey);

    const abortHandler = (): void => abort$.next();

    if (signal)
      signal.addEventListener('abort', abortHandler, { once: true });

    try {
      return await lastValueFrom(method().pipe(takeUntil(abort$)));
    } catch (e: unknown) {
      if (e instanceof HttpErrorResponse || e instanceof EmptyError) {
        throw e;
      }

      this.toastService.error("Erro interno no servidor.", "Oops...");
      console.warn('Erro silencioso na Api', e);
      return null;
    } finally {
      if (signal)
        signal.removeEventListener('abort', abortHandler);
      this.removeAbort(abortKey);
    }
  }

  private execute<T>(method: () => Observable<T>, abortKey: string, signal?: AbortSignal): Observable<T | null> {
    const abort$ = this.newAbort(abortKey);

    const abortHandler = (): void => abort$.next();

    if (signal)
      signal.addEventListener('abort', abortHandler, { once: true });


    return method().pipe(
      takeUntil(abort$),
      catchError((e: any) => {
        if (e instanceof HttpErrorResponse)
          return throwError(() => e);

        this.toastService.error("Erro interno no servidor.", "Oops...");
        console.warn('Erro silencioso na Api', e);

        return of(null);
      }),
      finalize(() => {
        if (signal)
          signal.removeEventListener('abort', abortHandler);

        this.removeAbort(abortKey)
      }),
    );
  }

  private async toAsyncResult<T>(promise: Promise<T | null>): Promise<ApiResult<T>> {
    try {
      const success = await promise;
      return { success: success ?? undefined };
    } catch (e: unknown) {
      if (e instanceof EmptyError)
        return { canceled: true };

      if (e instanceof HttpErrorResponse)
        return { error: e };

      return { error: new HttpErrorResponse({ status: 500 }) };
    }
  }

  private newAbort(key: string): Subject<void> {
    const abort$ = new Subject<void>();
    this.aborts.push({
      key,
      subject$: abort$,
    });

    return abort$;
  }

  private getClient(cancelInterceptors?: boolean): HttpClient {
    let client = this.httpClient;

    if (cancelInterceptors && this.httpBackend)
      client = new HttpClient(this.httpBackend);

    return client;
  }

}
